import React from "react";
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Typography,
  Stack,
  FormHelperText,
} from "@mui/material";
import { apiService } from "../../../config/api";
import { StudyService } from "../../../services/studyService";
import type { Participant } from "../../../types";
import { getDeviceSources, sourceLabel } from "../../../utils/deviceType";

interface ParticipantSelectorProps {
  studyId: string;
  selectedParticipant: Participant | null;
  onParticipantSelect: (participant: Participant | null) => void;
}

const ParticipantSelector: React.FC<ParticipantSelectorProps> = ({
  studyId,
  selectedParticipant,
  onParticipantSelect,
}) => {
  const [participants, setParticipants] = React.useState<Participant[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchParticipants = async () => {
      const token = await apiService.getAuthToken();
      if (!token) {
        setError("Sign in to load participants");
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await StudyService.getStudyParticipants(
          studyId,
          token,
        );

        if (response.success && Array.isArray((response as any).participants)) {
          const mappedParticipants: Participant[] = (
            response as any
          ).participants.map((participant: any) => ({
            ...participant,
            participantId: participant.participantId || participant.id,
          }));
          setParticipants(mappedParticipants);
        } else {
          setError("Failed to load participants");
        }
      } catch (err) {
        setError("Error loading participants");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchParticipants();
  }, [studyId]);

  const getDeviceTypes = (participant: Participant): string[] => {
    // Shared registry → extensible; a new source appears here automatically.
    const labels = getDeviceSources(participant).map(sourceLabel);
    return labels.length > 0 ? labels : ["No Device"];
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <FormControl
        fullWidth
        error={error !== null}
        disabled={loading || error !== null}
      >
        <InputLabel>Select Participant</InputLabel>
        <Select
          label="Select Participant"
          value={selectedParticipant?.participantId || ""}
          onChange={(e) => {
            const participantId = e.target.value;
            const participant = participants.find(
              (p) => p.participantId === participantId,
            );
            onParticipantSelect(participant || null);
          }}
        >
          <MenuItem value="">
            <em>-- Choose a participant --</em>
          </MenuItem>
          {participants.map((participant) => (
            <MenuItem
              key={participant.participantId}
              value={participant.participantId}
            >
              <Stack direction="column" spacing={0.5}>
                <Typography variant="body2">
                  {`${participant.firstName || ""} ${participant.lastName || ""}`.trim() ||
                    "Unknown"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {participant.participantEmail}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
        {loading && (
          <FormHelperText>
            <CircularProgress size={16} sx={{ mr: 1 }} /> Loading
            participants...
          </FormHelperText>
        )}
      </FormControl>

      {selectedParticipant && (
        <Box
          sx={{
            p: 2,
            bgcolor: "background.default",
            borderRadius: 1,
            border: "1px solid #e0e0e0",
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Selected Participant:
            </Typography>
            <Typography variant="body2">
              {`${selectedParticipant.firstName || ""} ${selectedParticipant.lastName || ""}`.trim() ||
                "Unknown"}{" "}
              ({selectedParticipant.participantEmail})
            </Typography>
            <Stack direction="row" spacing={1}>
              <Typography variant="caption" color="textSecondary">
                Connected Devices:
              </Typography>
              <Box display="flex" gap={0.5}>
                {getDeviceTypes(selectedParticipant).map((device) => (
                  <Chip
                    key={device}
                    label={device}
                    size="small"
                    sx={{
                      backgroundColor:
                        device === "Fitbit"
                          ? "rgba(15, 118, 110, 0.1)"
                          : device === "HealthKit"
                            ? "rgba(140, 21, 21, 0.1)"
                            : "rgba(128, 128, 128, 0.1)",
                      color:
                        device === "Fitbit"
                          ? "#0f766e"
                          : device === "HealthKit"
                            ? "#8C1515"
                            : "#808080",
                    }}
                  />
                ))}
              </Box>
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default ParticipantSelector;
